import { useEffect, useMemo } from 'react';
import { useNotify } from '@hotjar/hotsauce';

import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import {
  clearParameters,
  getParameter,
  getUrlPathAndSearch,
} from '@hotjar/insights-common/lib/url';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { mapSiteAccountModelToDto } from '@hotjar/account';
import { noop } from '@hotjar/webapp/lib/function';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';

import { logError } from '@hotjar/insights-common/lib/logger';
import { useTranslation } from '@hotjar/translations';
import { isKnownReferrer } from '@hotjar/webapp/domains/revenue/upsell/utils';

import type { Plan } from '@hotjar/webapp/domains/revenue/plan/plan';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { isUnifiedUpsellingEnabled } from '@hotjar/upsell';
import {
  updateDraftOrder,
  mapSelectedPlansToV3ProductAllowances,
} from '@hotjar/subscription-management';
import { useProductCatalog as useProductCatalogV2 } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { useProductCatalog as useProductCatalogV3 } from '@hotjar/product-catalog';
import {
  getProductSkusByPlanId,
  getProductBySku,
} from '@hotjar/webapp/domains/common/product/utils';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';

interface UseUpsellUrlParams {
  isLoading: boolean;
  currentSite: Site | undefined;
}

const URL_PARAMETERS = {
  UPGRADE_REQUEST: 'upgrade_request',
  REQUESTED_FROM: 'requested_from',
  IS_REQUEST: 'is_request',
};

/**
 * Checks the URL for upsell-related parameters, and triggers and upsell scenario if they are present. This is useful
 * for triggering upsells through URLs.
 *
 * Use cases:
 * 1. Upgrade requests - Non-owners can request upgrades in upsell modals. Their account owner will receive an email
 *                        that contains a link with URL parameters, that, if clicked, will allow them to approve and
 *                        finalize the upgrade.
 * 2. Marketing campaigns - It is possible to create URLs for the purpose of promoting an upgrade to a higher plan.
 *
 * URL parameters:
 * 1. `upgrade_request` - Legacy plan ID that we prompt the user to upgrade to
 * 2. `requested_from` - Upsell referrer string
 * 3. `is_request` - Whether or not we treat it as an upgrade request (use case #1), or a generic upsell-by-URL case.
 *                    (use case #2)
 *
 * By default, we validate the URL parameters, and we call the `upsell` function with data we could extract from the
 * parameters. This typically means that an upsell modal will open.
 *
 * If the site/account has billing v3 enabled and also has the unified upselling feature flag, we will convert the
 * selected legacy plan ID to v3 SKUs, save a draft order, and navigate to checkout. This way, the upgrade request can
 * be handled on the 'Manage subscriptions' page, instead of the upsell modals. The main reason for this is that
 * handling upgrade requests in an upsell modal introduces complexity and code branches for no great benefit, and this
 * is one way to eliminate the need for that.
 */
export function useUpsellUrlParams(): UseUpsellUrlParams {
  const { showToast } = useNotify();
  const { t } = useTranslation('revenue', { keyPrefix: 'upsell.plan-upsell' });

  const { data: currentSite, isLoading: isLoadingCurrentSite } = useCurrentSite();
  const siteAccount = currentSite?.account;

  const shouldUseDraftOrder =
    isBillingV3Enabled(siteAccount) && isUnifiedUpsellingEnabled(currentSite);

  const { data: productCatalogV2, isLoading: isLoadingProductCatalogV2 } = useProductCatalogV2({
    enabled: !!currentSite,
  });
  const { data: productCatalogV3, isLoading: isLoadingProductCatalogV3 } = useProductCatalogV3({
    billingPeriod: 'MONTHLY',
    currency: 'EUR',
    enabled: shouldUseDraftOrder,
  });

  const isLoading = isLoadingCurrentSite || isLoadingProductCatalogV2 || isLoadingProductCatalogV3;

  const referrer = getParameter(window.location.href, URL_PARAMETERS.REQUESTED_FROM);
  const requestedPlanId = getParameter(window.location.href, URL_PARAMETERS.UPGRADE_REQUEST);

  const isUpgradeRequest =
    getParameter(window.location.href, URL_PARAMETERS.IS_REQUEST) !== 'false';

  const planRepository = usePlanRepository();

  // Removes the query parameters from the URL, so that when the page is reloaded, we don't retrigger the upsell.
  const clearUpgradeRequestParameters = () => {
    const nextUrl = getUrlPathAndSearch(
      clearParameters(window.location.href, Object.values(URL_PARAMETERS)),
    );
    browserHistory.replace(nextUrl);
  };

  const accountDto = useMemo(
    () => (siteAccount ? mapSiteAccountModelToDto(siteAccount) : undefined),
    [siteAccount],
  );

  let plan: Plan | null = null;

  // planRepository.byId can throw an error if it cannot find a plan with the requested plan id.
  // This can happen because we get the plan id from search params, and it can potentially be wrong.
  // This can also cause a change in the  order of Hooks called and cause other hidden errors
  try {
    plan = planRepository.byId(requestedPlanId);
  } catch (e) {
    plan = null;
  }

  const { pricingTable, planGt } = usePricingTableByAccount(accountDto);

  useEffect(() => {
    // Return data are not ready yet
    if (isLoadingCurrentSite || !currentSite || !referrer) {
      return;
    }

    // Early out if the referrer is not supported by the upsell function
    // We log these errors, so we can monitor them in Sentry so that we can know
    // early if we need to support these referrers (e.g. new sales campaign that
    // we did not know of)
    if (!isKnownReferrer(referrer)) {
      logError(new Error(`useUpsellUrlParams: called with unsupported referrer ${referrer}`));
      return;
    }

    // if there is no requestedPlanId, and it's not an upgrade request, then we do serve the modal on their default/regular state
    // This is mostly for marketing teams when they need to promote the default state of a modal for a campaign.
    if (!requestedPlanId && !isUpgradeRequest) {
      clearUpgradeRequestParameters();
      upsell({
        site: currentSite,
        referrer,
        isUpgradeRequest: false,
      }).then(noop, noop);
      return;
    }

    // Return if we could not resolve the planId to a plan object
    if (!plan) {
      return;
    }

    // We need to check that the requested plan is available based on the accounts
    // pricing table version.
    if (!pricingTable[plan.identifier]) {
      return;
    }

    clearUpgradeRequestParameters();

    // Show a toast message when requesting a lower or the current Plan
    if (!planGt(plan.identifier, currentSite.planId)) {
      showToast({ text: t('already-upgraded'), kind: 'info' });
      return;
    }

    if (shouldUseDraftOrder) {
      const [selectedAnalyticsProductSku, selectedFeedbackProductSku] = getProductSkusByPlanId(
        plan.identifier,
      );
      const selectedAnalyticsProduct = getProductBySku(
        selectedAnalyticsProductSku,
        productCatalogV2,
      );
      const selectedFeedbackProduct = getProductBySku(selectedFeedbackProductSku, productCatalogV2);

      if (!selectedAnalyticsProduct || !selectedFeedbackProduct) {
        logError(
          new Error(
            `useUpsellUrlParams: Failed to resolve product objects from v2 catalog by legacy plan ID: ${plan.identifier}`,
          ),
        );
        return;
      }

      const payload = mapSelectedPlansToV3ProductAllowances({
        selectedPlans: {
          [currentSite.id]: {
            [ProductType.Analytics]: selectedAnalyticsProduct,
            [ProductType.Feedback]: selectedFeedbackProduct,
          },
        },
        sites: [currentSite],
        productCatalog: productCatalogV3,
      });

      updateDraftOrder(
        {
          type: 'products',
          payload,
        },
        {
          checkoutOnSuccess: true,
        },
      ).then(noop, noop);
    } else {
      upsell({
        site: currentSite,
        referrer,
        recommendedPlanId: plan.identifier,
        isUpgradeRequest,
      }).then(noop, noop);
    }
  }, [
    currentSite,
    isLoadingCurrentSite,
    isUpgradeRequest,
    plan,
    planGt,
    pricingTable,
    productCatalogV2,
    productCatalogV3,
    referrer,
    requestedPlanId,
    shouldUseDraftOrder,
    showToast,
    t,
  ]);

  return {
    isLoading,
    currentSite,
  };
}
